/**
 * What the funnel learns from `GET /public/tenant` (MASTER_PLAN M5.7, FR-PLAT-03,
 * FR-BOOK-03/04/05, API.md §2.3).
 *
 * The state list, the visit types, the consent texts and the booking window all belong
 * to the clinic, not to this bundle. Until M5.7 they were eight hard-coded names in
 * `lib/states.ts` and two hard-coded buttons in `book-page.tsx`; now they come from the
 * boot response and those constants are the FALLBACK — the answer a component gives
 * before the tenant has arrived, and the answer the parity build gives, which is why
 * they are the live copy to the letter (ADR-008).
 *
 * ## Why a module store and not `useTenant()` in every page
 *
 * `useTenant()` is a TanStack Query hook and therefore needs a provider. The funnel's
 * components are rendered bare in the parity regression test and in twenty behaviour
 * tests, and a page that could only render inside a provider would make every one of
 * them about plumbing. `flags.ts` solved the same problem the same way, and this is
 * deliberately its twin: a module store with `useSyncExternalStore`, a `read*` for the
 * non-React call sites, and a documented seam (`setBookingConfig`) that the boot calls
 * exactly once.
 *
 * Nothing here is an authorization decision, and nothing here is PHI.
 */

import { useSyncExternalStore } from 'react';

import type { PublicTenant } from '@platform/api-client';

import { LICENSED_STATES, stateCode, stateName } from './states';
import { CLINIC_TZ } from './time';

/** One option in the state gate: the code the API speaks, the name the patient reads. */
export interface FunnelStateOption {
  code: string;
  name: string;
}

/**
 * A visit type as the funnel needs it.
 *
 * `serviceName` and `requiresPaperwork` are not in the tenant payload and are derived
 * here, because the live funnel's copy depends on them:
 *
 * - `serviceName` is the line `/payment` prints under "Service". Live it is
 *   "New Patient Consultation" / "Follow-up Appointment"; for a tenant type it is the
 *   type's own `name`, which is the only name the clinic has told us.
 * - `requiresPaperwork` is whether `/payment`'s success panel offers "Complete
 *   Paperwork →". Live, only the first (new-patient) type does. The tenant response
 *   carries no such field, so it is taken from the type's position in the clinic's own
 *   order — the same order the seed sorts by.
 *   TODO(M8.7): the intake assignment decides this, and the derivation goes away.
 */
export interface FunnelAppointmentType {
  /** The uuid, when the tenant gave one; otherwise the key. Never shown. */
  id: string;
  /** The stable key. `type=` on availability and `appointmentTypeKey` on the draft. */
  key: string;
  /** The toggle's label. */
  name: string;
  /** The toggle's `<small>` line. */
  description: string;
  durationMinutes: number;
  selfPayPriceCents: number;
  currency: string;
  requiresInsuranceStep: boolean;
  serviceName: string;
  requiresPaperwork: boolean;
}

/** A consent the patient must be shown before they agree to anything (API.md §2.3). */
export interface FunnelConsent {
  kind: 'telehealth' | 'hipaa_notice' | 'financial' | 'unsecured_email' | 'sms' | 'marketing';
  version: string;
  /** Null until the clinic writes the text; a consent with no text is not rendered. */
  text: string | null;
  required: boolean;
}

export interface FunnelBookingWindow {
  minLeadDays: number;
  maxHorizonDays: number;
  /** Minutes a slot is held during checkout. */
  reservationMinutes: number;
}

export interface BookingConfig {
  /** False until `GET /public/tenant` has answered. The parity build never flips it. */
  loaded: boolean;
  clinicTimeZone: string;
  states: FunnelStateOption[];
  appointmentTypes: FunnelAppointmentType[];
  consents: FunnelConsent[];
  bookingWindow: FunnelBookingWindow;
  legal: {
    privacyUrl: string | null;
    termsUrl: string | null;
    financialPolicyUrl: string | null;
  };
  /**
   * The clinic's own contact points, from `branding.supportPhone` / `supportEmail` (M15).
   * The scheduling-rules "contact the office" panel is the one funnel screen that prints
   * them; null when the clinic has not set one, and then that line is simply absent.
   */
  support: { phone: string | null; email: string | null };
}

/** The key the funnel uses for the first visit type before a tenant is loaded. */
export const PARITY_NEW_PATIENT_KEY = 'new_patient';
/** And for the second. */
export const PARITY_FOLLOW_UP_KEY = 'follow_up';

/**
 * The parity answer: the live page's own two types and eight states, and the window the
 * live page's `BK_CONFIG` used (2 days out to 30, which is the 21 chips FR-BOOK-05 and
 * the frozen DOM outline both expect).
 */
export const PARITY_BOOKING_CONFIG: Readonly<BookingConfig> = Object.freeze({
  loaded: false,
  clinicTimeZone: CLINIC_TZ,
  states: LICENSED_STATES.map((name) => ({ code: stateCode(name) ?? name, name })),
  appointmentTypes: [
    {
      id: PARITY_NEW_PATIENT_KEY,
      key: PARITY_NEW_PATIENT_KEY,
      name: 'New Patient',
      description: 'First visit',
      durationMinutes: 30,
      selfPayPriceCents: 0,
      currency: 'USD',
      requiresInsuranceStep: true,
      serviceName: 'New Patient Consultation',
      requiresPaperwork: true,
    },
    {
      id: PARITY_FOLLOW_UP_KEY,
      key: PARITY_FOLLOW_UP_KEY,
      name: 'Follow-up',
      description: 'Existing patient',
      durationMinutes: 30,
      selfPayPriceCents: 0,
      currency: 'USD',
      requiresInsuranceStep: true,
      serviceName: 'Follow-up Appointment',
      requiresPaperwork: false,
    },
  ],
  consents: [],
  bookingWindow: { minLeadDays: 2, maxHorizonDays: 30, reservationMinutes: 10 },
  legal: { privacyUrl: null, termsUrl: null, financialPolicyUrl: null },
  support: { phone: null, email: null },
} satisfies BookingConfig);

let snapshot: BookingConfig = PARITY_BOOKING_CONFIG;
const listeners = new Set<() => void>();

function republish(next: BookingConfig): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): BookingConfig {
  return snapshot;
}

/** The resolved answer, for the call sites that are not components. */
export function readBookingConfig(): BookingConfig {
  return snapshot;
}

/**
 * Be told when the tenant arrives, without importing the store that wants to know.
 *
 * `store.ts` reads this module (to translate a state code, to name a visit type), so it
 * cannot be imported back. Exposing the subscription inverts the dependency: the store
 * subscribes at module load and reconciles its own draft, and nothing here knows that a
 * draft exists.
 */
export function subscribeBookingConfig(listener: () => void): () => void {
  return subscribe(listener);
}

/** The resolved answer, with a re-render when the tenant arrives. */
export function useBookingConfig(): BookingConfig {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * The M5.7 seam. Called once, from `applyTenantBranding`, with the boot response; `null`
 * puts the funnel back on the parity answer.
 *
 * A field the tenant left empty falls back rather than blanking the funnel: a clinic
 * with no visit types configured must still render a usable page, and an empty state
 * gate is a page that cannot be used at all.
 */
export function setBookingConfig(tenant: PublicTenant | null): void {
  if (tenant === null) {
    republish(PARITY_BOOKING_CONFIG);
    return;
  }

  const states = readStates(tenant);
  const appointmentTypes = readAppointmentTypes(tenant);

  republish({
    loaded: true,
    clinicTimeZone: tenant.tenant.timeZone === '' ? CLINIC_TZ : tenant.tenant.timeZone,
    states: states.length > 0 ? states : [...PARITY_BOOKING_CONFIG.states],
    appointmentTypes:
      appointmentTypes.length > 0 ? appointmentTypes : [...PARITY_BOOKING_CONFIG.appointmentTypes],
    consents: readConsents(tenant),
    bookingWindow: readBookingWindow(tenant),
    legal: {
      privacyUrl: tenant.legal?.privacyUrl ?? null,
      termsUrl: tenant.legal?.termsUrl ?? null,
      financialPolicyUrl: tenant.legal?.financialPolicyUrl ?? null,
    },
    support: {
      phone: nonBlank(tenant.branding.supportPhone),
      email: nonBlank(tenant.branding.supportEmail),
    },
  });
}

/** Test seam: back to the parity answer. `vitest.setup.ts` calls it after every test. */
export function resetBookingConfigForTest(): void {
  republish(PARITY_BOOKING_CONFIG);
}

/** A trimmed string, or null for absent and blank alike. */
function nonBlank(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/* ------------------------------------------------------------------ *
 * Lookups
 * ------------------------------------------------------------------ */

/** The visit type with this key, or the first one, or `undefined` if there are none. */
export function appointmentTypeFor(
  key: string,
  config: BookingConfig = snapshot,
): FunnelAppointmentType | undefined {
  return config.appointmentTypes.find((type) => type.key === key) ?? config.appointmentTypes[0];
}

/** The key the funnel starts on: the clinic's first visit type. */
export function defaultAppointmentTypeKey(config: BookingConfig = snapshot): string {
  return config.appointmentTypes[0]?.key ?? PARITY_NEW_PATIENT_KEY;
}

/** The name behind a code the tenant or a saved draft gave us. */
export function stateNameFor(code: string, config: BookingConfig = snapshot): string {
  return config.states.find((option) => option.code === code)?.name ?? stateName(code);
}

/* ------------------------------------------------------------------ *
 * Reading the payload
 * ------------------------------------------------------------------ */

/**
 * The clinic's states, in the clinic's own order.
 *
 * The order is NOT re-sorted. The tenant is the authority on how it wants its own
 * option list read, and a funnel that quietly alphabetised it would be a funnel whose
 * rendering nobody could predict from the response.
 */
function readStates(tenant: PublicTenant): FunnelStateOption[] {
  const raw = Array.isArray(tenant.states) ? tenant.states : tenant.tenant.statesServed;
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const out: FunnelStateOption[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string' || entry.trim() === '') continue;
    const code = entry.trim().toUpperCase();
    if (seen.has(code)) continue;
    seen.add(code);
    out.push({ code, name: stateName(code) });
  }
  return out;
}

function readAppointmentTypes(tenant: PublicTenant): FunnelAppointmentType[] {
  const raw = tenant.appointmentTypes;
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((type) => typeof type?.key === 'string' && type.key !== '')
    .map((type, index) => ({
      id: type.id === '' ? type.key : type.id,
      key: type.key,
      name: type.name === '' ? type.key : type.name,
      description: type.description,
      durationMinutes: type.durationMinutes,
      selfPayPriceCents: type.selfPayPriceCents,
      currency: type.currency,
      requiresInsuranceStep: type.requiresInsuranceStep,
      serviceName: type.name === '' ? type.key : type.name,
      requiresPaperwork: index === 0,
    }));
}

function readConsents(tenant: PublicTenant): FunnelConsent[] {
  const raw = tenant.legal?.consents;
  if (!Array.isArray(raw)) return [];

  /* A consent with no text is not something a patient can agree to, so it is skipped
     rather than rendered as an empty tick box (API.md §2.3 skips one with no version). */
  return raw.filter((consent) => typeof consent.text === 'string' && consent.text.trim() !== '');
}

function readBookingWindow(tenant: PublicTenant): FunnelBookingWindow {
  const window = tenant.bookingWindow;
  const fallback = PARITY_BOOKING_CONFIG.bookingWindow;
  if (window === undefined || window === null) return { ...fallback };

  const read = (value: unknown, min: number, or: number): number =>
    typeof value === 'number' && Number.isFinite(value) && value >= min ? Math.trunc(value) : or;

  return {
    minLeadDays: read(window.minLeadDays, 0, fallback.minLeadDays),
    maxHorizonDays: read(window.maxHorizonDays, 1, fallback.maxHorizonDays),
    reservationMinutes: read(window.reservationMinutes, 1, fallback.reservationMinutes),
  };
}
